import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { useEventStore } from "store";
import { FormattedMessage } from "react-intl";

type Props = {
  file: File | null;
  onFinish: (file: File) => void;
  onCancel: () => void;
  maxZoom?: number;
};

type ImgCroppedArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export default function ImageCropper({
  file,
  onFinish,
  onCancel,
  maxZoom,
}: Props) {
  const { theme } = useEventStore();
  const [image, setImage] = useState<any>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<ImgCroppedArea>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedArea(croppedAreaPixels);
  };

  const onZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };

  const confirmCropHandler = useCallback(() => {
    const canvasEle = document.createElement("canvas");
    canvasEle.width = 960;
    canvasEle.height = 960;

    const context = canvasEle.getContext("2d");
    const imageCropped = new Image();
    imageCropped.src = image;
    imageCropped.onload = function () {
      context?.drawImage(
        imageCropped,
        croppedArea.x,
        croppedArea.y,
        croppedArea.width,
        croppedArea.height,
        0,
        0,
        canvasEle.width,
        canvasEle.height
      );

      const originalFileName = file?.name || "post-img.jpg";
      const fileExtension =
        originalFileName.split(".").pop()?.toLowerCase() || "jpg";
      const fileType = file?.type || "image/jpg";
      const outputQuality = 0.8;
      const croppedFileName = `img-cropped.${fileExtension}`;

      canvasEle.toBlob(
        async (blob) => {
          if (!blob) {
            return;
          }
          const croppedFile = new File([blob], croppedFileName, {
            type: fileType,
            lastModified: Date.now(),
          });

          onFinish(croppedFile);
          setImage(null);
        },
        fileType,
        outputQuality
      );
    };
  }, [croppedArea, image, onFinish, file]);

  useEffect(() => {
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setImage(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  }, [file]);

  return (
    <div className="relative h-[500px] w-full max-w-[400px]">
      <div className="absolute w-full md:w-[400px] h-[400px]">
        <Cropper
          image={image}
          aspect={1 / 1}
          crop={crop}
          zoom={zoom}
          maxZoom={maxZoom || 2}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      <div className="absolute bottom-0 w-full">
        <div className="flex flex-col items-start justify-center">
          <input
            type="range"
            className={`p-0 mb-3 w-full h-2 ${
              theme.name === "rose" ? "bg-rose-200" : "bg-gray-200"
            } rounded-lg appearance-none cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-5
            [&::-webkit-slider-thumb]:h-5
            [&::-webkit-slider-thumb]:rounded-full
             ${
               theme.name === "rose"
                 ? "[&::-webkit-slider-thumb]:bg-primary-rose"
                 : "[&::-webkit-slider-thumb]:bg-gray-600"
             }`}
            min={1}
            max={maxZoom || 2}
            step={0.001}
            value={zoom}
            onChange={onZoomChange}
          />

          <div className="mt-3 w-full flex items-center justify-around">
            <button
              onClick={onCancel}
              className={`${
                theme.name === "rose" ? theme.buttonSecondary : theme.button
              } shadow-md shadow-gray-300 font-century-gothic-bold text-sm rounded-[10px] text-theme-black h-min px-10 x flex justify-center items-center`}
            >
              <FormattedMessage
                id="feed.button.options.cancel"
                defaultMessage="Cancelar"
              />
            </button>
            <button
              onClick={confirmCropHandler}
              className={`${
                theme.name === "rose" ? theme.button : theme.buttonSecondary
              }  shadow-md shadow-gray-300 font-century-gothic-bold text-sm rounded-[10px] text-theme-black h-min px-10 x flex justify-center items-center`}
            >
              <FormattedMessage
                id="feed.button.options.confirm"
                defaultMessage="Confirmar"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
